import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '@app/pages/dashboard/initiative/shared/workflow'
import { CreationStepLayout } from '../blocks/CreationStepLayout'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { enrich } from '@i18n/localizeHelper'
import { Form } from '@app/components/core/form/Form'
import { TextField } from '@app/components/core/form/fields/TextField'
import { TextAreaField } from '@app/components/core/form/fields/TextAreaField'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'
import {
  createLivingVisionTextSchema,
  VISION_MIN_LENGTH,
  type LivingVisionTextFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'
import { useInitiativeFormWatchAutosave } from '@app/pages/dashboard/initiative/shared/useInitiativeFormWatchAutosave'
import {
  isOverviewPageContentStepValid,
  mapOverviewTextBlocksOptional,
} from '@app/pages/dashboard/initiative/shared/initiativeOverviewTextBlocks'
import { useInitiativePageContentBlocksSave } from '@app/pages/dashboard/initiative/shared/useInitiativePageContentBlocksSave'
import {
  PageContentEditor,
  type PageContentEditorBlock,
} from '@app/components/blocks/pageContentEditor/PageContentEditor'
import { Style } from 'inlines'

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

const CURRENT_STEP = initiativeCreationSteps.livingVision

export const LivingVisionStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const view = useTranslations().app.dashboard.initiative.view
  const formValidation = useTranslations().app.form.validation

  const serverBlocks = useMemo(
    () => mapOverviewTextBlocksOptional(initiative.contentBlocks),
    [initiative.contentBlocks],
  )

  const [blocks, setBlocks] = useState<PageContentEditorBlock[]>(serverBlocks)

  useEffect(() => {
    setBlocks(serverBlocks)
  }, [serverBlocks])

  useInitiativePageContentBlocksSave(initiative.id, serverBlocks, blocks)

  const schema = useMemo(
    () =>
      createLivingVisionTextSchema(
        view.initiativeNameMinLength,
        enrich(formValidation.minLength, VISION_MIN_LENGTH),
      ),
    [view.initiativeNameMinLength, formValidation.minLength],
  )

  const defaultValues = useMemo(
    (): LivingVisionTextFormValues => ({
      title: initiative.title ?? '',
      description: initiative.description ?? '',
      vision: initiative.vision ?? '',
    }),
    [initiative.title, initiative.description, initiative.vision],
  )

  return (
    <CreationStepLayout
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <Form<LivingVisionTextFormValues>
        key={initiative.id}
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <LivingVisionOnboardingEffects
          initiativeId={initiative.id}
          blocks={blocks}
          completeStep={completeStep}
          uncompleteStep={uncompleteStep}
        />
        <Column gap="2rem" alignItems="stretch">
          <TextField<LivingVisionTextFormValues>
            name="title"
            label={translations.nameLabel}
            required={true}
            labelStyle={labelStyle}
            placeholder={translations.namePlaceholder}
          />
          <TextField<LivingVisionTextFormValues>
            name="description"
            label={translations.sloganLabel}
            required={false}
            labelStyle={labelStyle}
            placeholder={translations.sloganPlaceholder}
          />
          <TextAreaField<LivingVisionTextFormValues>
            name="vision"
            label={translations.livingVisionLabel}
            description={translations.pageContentSubtitle}
            required={true}
            labelStyle={labelStyle}
            placeholder={translations.visionSummaryPlaceholder}
          />
          <PageContentEditor
            value={blocks}
            minBlocks={0}
            onValueChange={setBlocks}
            blockEditorVariant="creation"
            addBlockVariant="button"
            titlePlaceholder={translations.livingVisionTextBlockTitlePlaceholder}
            bodyPlaceholder={translations.visionSummaryPlaceholder}
          />
        </Column>
      </Form>
    </CreationStepLayout>
  )
}

const LivingVisionOnboardingEffects = ({
  initiativeId,
  blocks,
  completeStep,
  uncompleteStep,
}: {
  initiativeId: number
  blocks: PageContentEditorBlock[]
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}): null => {
  const { control, formState } = useFormContext<LivingVisionTextFormValues>()
  const { isValid } = formState
  const blocksValid =
    blocks.length === 0 || isOverviewPageContentStepValid(blocks)

  useInitiativeFormWatchAutosave({
    initiativeId,
    control,
    toPayload: (values) => ({
      title: values.title,
      description: values.description,
      vision: values.vision,
      onboardingStep: CURRENT_STEP,
    }),
    watchDeps: (values) =>
      [values.title, values.description, values.vision] as const,
  })

  useEffect(() => {
    if (isValid && blocksValid) {
      completeStep(CURRENT_STEP)
    } else {
      uncompleteStep(CURRENT_STEP)
    }
  }, [isValid, blocksValid, completeStep, uncompleteStep])

  return null
}
